import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';
import { transportFailureRetry } from '../../utils/retryTransportFailures';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';

const DirectoryEntrySchema = z.object({
  identifier: z.string(),
  name: z.string(),
  type: z.enum(['conference', 'transfer']),
  tt911_capability_enabled: z.boolean(),
});
const DirectoryResponseSchema = z.array(DirectoryEntrySchema);

/**
 * Why a directory agency cannot receive a soft transfer. An agency can be
 * ineligible for more than one reason at once.
 */
export type IneligibilityReason =
  | 'not_conference'
  | 'tt911_capability_disabled';

/**
 * Mirrors the pydantic ``TransferState`` model in
 * ``rsos/services/data/text_to_911/tcapi/models.py``. The backend uses
 * ``DualCaseModel`` so the wire payload includes both snake_case and
 * camelCase keys; we read snake_case to stay consistent with the rest of
 * the TCAPI session payload and with the canonical pydantic form.
 */
const TCAPISessionTransferSchema = z.object({
  status: z.enum(['pending', 'accepted', 'timed_out']),
  initiating_authority_account_id: z.string(),
  initiating_authority_display_name: z.string().nullable(),
  receiving_authority_account_id: z.string().nullable(),
  receiving_authority_display_name: z.string().nullable(),
});

/**
 * Mirrors the pydantic ``SessionResponse`` model in
 * ``rsos/services/data/text_to_911/tcapi/models.py``. Top-level fields use
 * snake_case (the canonical pydantic form, which SOUP-1191 standardized
 * the frontend on); the dual-case backend also emits camelCase variants
 * which ``z.object`` strips by default.
 */
const TCAPISessionResponseSchema = z.object({
  server_time_millis: z.number(),
  conversation_id: z.string(),
  authority_account_id: z.string(),
  authority_display_name: z.string(),
  twilio_phone: z.string().nullable(),
  caller_id: z.string(),
  creation_time: z.number().nullable(),
  last_updated_time: z.number().nullable(),
  translation_method: z.string().nullable(),
  last_caller_language: z.string().nullable(),
  override_caller_language: z.string().nullable(),
  outbound_translation: z.boolean(),
  status: z.enum(['pending', 'active', 'ended', 'timed_out']),
  session_type: z.enum([
    'sms',
    'convey',
    'rcs',
    'sms_admin',
    'tcapi',
    'interagency',
  ]),
  expire_time: z.number().nullable(),
  last_accept_time: z.number().nullable(),
  session_id: z.string(),
  transfer: TCAPISessionTransferSchema.nullable(),
  agency_participants: z.array(z.string()).default([]),
  // Server's answer on whether a call-back may still re-engage this
  // session. Optional so a backend that predates the field still parses;
  // absent is read as callable at the call site.
  can_call_back: z.boolean().optional(),
});

export type TCAPISessionTransfer = z.infer<typeof TCAPISessionTransferSchema>;
export type TCAPISessionResponse = z.infer<typeof TCAPISessionResponseSchema>;

interface GetTCAPIConversationMetadataArgs {
  /** Caller's phone number (digits only — the leading ``+`` is added by the query). */
  callerID: string;
  /** Name of the authority the user belongs to. */
  orgName: string;
}

const getTCAPIBaseEndpoint = (orgName: string) => {
  return `v1/text_to_911/tcapi/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;
export const TWO_HOURS = 1000 * 60 * 60 * 2;

/**
 * Extends the rsosApi for TCAPI endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const tcapiAPI = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIConversationMetadata: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({ callerID, orgName }: GetTCAPIConversationMetadataArgs) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        TCAPISessionResponseSchema.parse(response),
      providesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      // 404 = "no active session" — normal state for any SMS call that
      // hasn't been opened as a TCAPI session yet. Tag the error so
      // rtkQueryErrorMiddleware skips the unhandled-error toast/log; the
      // consumer still receives the rejection and can branch on it.
      transformErrorResponse: (error: FetchBaseQueryError) =>
        typeof error.status === 'number' && error.status === 404
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        // Fetched once per SMS call with no poll behind it, so a single dropped
        // packet would otherwise cost the operator the whole chat. 404 ("no
        // active session") and every other answered status still run once.
        ...transportFailureRetry,
      },
    }),
    /**
     * Ends a conversation with the given phone number. Optionally includes an
     * already-interpolated end_message.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     * @param endMessage - Optional pre-interpolated ending message body
     * @param doNotTranslate - Resolved smart-phrase values in endMessage to
     *   shield from translation
     */
    endTCAPIConversation: builder.mutation({
      query: ({
        callerID,
        orgName,
        endMessage,
        doNotTranslate,
      }: {
        callerID: string;
        orgName: string;
        endMessage?: string;
        doNotTranslate?: string[];
      }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: {
          status: 'ended',
          ...(endMessage !== undefined ? { end_message: endMessage } : {}),
          ...(doNotTranslate?.length
            ? { do_not_translate: doNotTranslate }
            : {}),
        },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Starts a conversation with the given phone number. The conversations
     * slice owns the per-caller ``createConversation`` loading flag via
     * extraReducer matchers on this mutation's pending/fulfilled/rejected
     * lifecycle.
     */
    createTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { status: 'active' },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Calls a caller back over the native TCAPI channel after their session
     * ended. Re-engages the existing (ended) session via a ``startCall``,
     * reusing the same conversation so the chat history is preserved.
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     */
    callBackTCAPIConversation: builder.mutation({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PUT',
        body: { action: 'call_back' },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      // 409 = the backend refused on purpose: either a call-back for this
      // session is already in flight, or the session is past the window it can
      // be restarted in. Both are outcomes the product intends, so tag them to
      // keep rtkQueryErrorMiddleware from reporting them. The consumer still
      // receives the rejection and shows the reason. Other statuses are still
      // reported.
      transformErrorResponse: (error: FetchBaseQueryError) =>
        typeof error.status === 'number' && error.status === 409
          ? { ...error, isExpectedError: true }
          : error,
      extraOptions: {
        ...baseExtraOptions,
        // Disable automatic retries, for every status rather than just the
        // refusals. Originating a call is not idempotent: the vendor rings
        // the caller before it answers us, so a failure we see can mean the
        // leg went out and the response was lost — a retry would dial a
        // citizen twice. The backend already retries its own forwarding
        // before answering. A refusal is deterministic on top of that, so
        // retrying one only delays the reason reaching the dispatcher.
        maxRetries: 0,
      },
    }),
    /**
     * Fetches the agency directory for transfer/conference.
     *
     * Every entry is returned, including ones that cannot receive a soft
     * transfer — the dialog shows those disabled with the reason rather than
     * hiding them, so a short or empty list is never unexplained. Only
     * `conference` entries are transfer-eligible; an agency registered with
     * the wrong type on the INdigital side is the common cause of a row being
     * ineligible.
     * @param orgName - Name of authority the user belongs to
     */
    getTCAPIAgencyDirectory: builder.query({
      query: ({ orgName }: { orgName: string }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/tcapi/agencies/${orgName}/directory`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const entries = DirectoryResponseSchema.parse(response);
        return entries.map(entry => {
          const ineligibilityReasons: IneligibilityReason[] = [];
          // `transfer` entries are fully eligible for a blind transfer —
          // INdigital already vetted the destination when it appears in the
          // directory with this type, so they're never gated on
          // tt911_capability_enabled the way `conference` entries are.
          // `type` is a 2-value enum (conference | transfer), so this only
          // ever applies to `conference` entries.
          if (entry.type === 'conference' && !entry.tt911_capability_enabled) {
            ineligibilityReasons.push('tt911_capability_disabled');
          }
          return { ...entry, ineligibilityReasons };
        });
      },
      providesTags: ['TCAPIAgencyDirectory'],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Initiates a transfer to another agency — either a soft transfer
     * (`conference`, stays in the chat) or a blind transfer (`transfer`,
     * leaves Unite entirely).
     * @param callerID - phone number
     * @param orgName - Name of authority the user belongs to
     * @param receiving_agency_identifier - identifier of the target agency
     * @param receiving_agency_name - display name of the target agency
     * @param transfer_type - 'conference' for a soft transfer, 'transfer' for a blind transfer
     */
    transferTCAPIConversation: builder.mutation({
      query: ({
        callerID,
        orgName,
        receiving_agency_identifier,
        receiving_agency_name,
        transfer_type,
      }) => ({
        ...baseQueryArgs,
        url: `${getTCAPIBaseEndpoint(orgName)}/session/+${callerID}`,
        method: 'PATCH',
        body: {
          receiving_agency_identifier,
          receiving_agency_name,
          transfer_type,
        },
      }),
      invalidatesTags: (_result, _error, { callerID }) => [
        { type: 'TCAPIMetaData', id: callerID },
      ],
      extraOptions: {
        ...baseExtraOptions,
        // Disable automatic retries — a failed transfer should surface the
        // error to the user immediately rather than silently retrying.
        maxRetries: 0,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetTCAPIConversationMetadataQuery,
  useLazyGetTCAPIConversationMetadataQuery,
  useEndTCAPIConversationMutation,
  useCreateTCAPIConversationMutation,
  useCallBackTCAPIConversationMutation,
  useGetTCAPIAgencyDirectoryQuery,
  useTransferTCAPIConversationMutation,
} = tcapiAPI;

/**
 * Action creator that invalidates the cached TCAPI session metadata for a
 * given caller, forcing the active ``getTCAPIConversationMetadata`` query
 * to re-fetch. Returned action must be dispatched.
 */
export function invalidateTCAPIConversationMetadata(callerID: string) {
  return tcapiAPI.util.invalidateTags([
    { type: 'TCAPIMetaData', id: callerID },
  ]);
}
